"use client";

/**
 * useCreditEstimate — read-only credit estimate for a pending upload.
 *
 * Calls POST /media/estimate (batch mode) so the server is the single source of
 * truth for credit pricing and the standard/premium tier. The client never
 * computes credit pricing or sees engine names. Used only in credit mode; the
 * legacy dollar path keeps computing locally until migration.
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { mediaKeys } from "@/queries/keys";

export type CreditTier = "standard" | "premium";

/** One pending file, described by client-side metadata known before upload. */
export interface CreditEstimateItem {
  durationSeconds?: number;
  mimeType?: string;
  sourceLanguage?: string;
  sizeBytes?: number;
  uploadType?: string;
  mediaCategory?: "audio" | "video" | "text";
  isTranslation?: boolean;
  enableDubbing?: boolean;
  characters?: number;
  /** Media already exists and is transcribed: bill translation/dubbing only. */
  alreadyTranscribed?: boolean;
  /** YouTube import, which carries a flat credit surcharge. */
  isYoutubeImport?: boolean;
}

export interface CreditEstimateItemResult {
  credits: number;
  tier: CreditTier;
  transcriptionCredits: number;
  translationCredits: number;
  dubbingCredits: number;
  /** Flat YouTube import surcharge, 0 for every other source. Absent on older servers. */
  sourceCredits?: number;
  isMeeting: boolean;
  approximate: boolean;
}

export interface CreditEstimate {
  available: true;
  unit: "credits";
  credits: number;
  tier: CreditTier;
  breakdown: {
    transcription: number;
    translation: number;
    dubbing: number;
    /** Flat per-import surcharge for metered sources (YouTube). Absent on older servers. */
    source?: number;
  };
  isMeeting: boolean;
  /** True when any file's language is auto-detected, so the tier is assumed, not known. */
  approximate: boolean;
  items: CreditEstimateItemResult[];
}

export function useCreditEstimate(items: CreditEstimateItem[], enabled: boolean) {
  return useQuery({
    queryKey: mediaKeys.estimate(items),
    queryFn: async () => {
      const res = await apiClient.post<CreditEstimate>("/media/estimate", { items });
      if (res.success && res.data) return res.data;
      throw new Error(res.error ?? "Failed to estimate credits");
    },
    enabled: enabled && items.length > 0,
    staleTime: 30 * 1000,
  });
}
